import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeftRight,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  FileText,
  Filter,
  Pencil,
  Plug,
  Plus,
  Search,
  StopCircle,
  Undo,
} from "lucide-react";

import { ApiError, formatDate } from "../../lib/api";
import { getAuth, hasPermission } from "../../lib/auth";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { fetchContratos, type ContratoOperativo } from "../socios/api";
import { listarSocios } from "../socios/api";
import { listarArranques } from "../arranques/api";
import { listarTarifas, type TarifaRead } from "../tarifas/api";
import {
  actualizarContrato,
  crearContrato,
  finalizarContrato,
  obtenerContrato,
  reactivarContrato,
  type ContratoCreatePayload,
  type ContratoRead,
  type ContratoUpdatePayload,
  type EstadoContrato,
} from "./api";
import { ConexionModal } from "./ConexionModal";
import { TraspasoModal } from "./TraspasoModal";
import { hoyLocalISO } from "../../lib/fechaLocal";

const ESTADOS_FILTRO: { value: "TODOS" | EstadoContrato; label: string }[] = [
  { value: "TODOS", label: "Todos los estados" },
  { value: "Activo", label: "Activos" },
  { value: "Cortado", label: "Cortados" },
  { value: "Finalizado", label: "Finalizados" },
];

export function ContratosListPage(): JSX.Element {
  const [busqueda, setBusqueda] = useState("");
  const [params, setParams] = useSearchParams();
  const estadoUrl = params.get("estado");
  const [filtroEstado, setFiltroEstado] = useState<"TODOS" | EstadoContrato>(
    estadoUrl === "Activo" || estadoUrl === "Cortado" || estadoUrl === "Finalizado"
      ? estadoUrl
      : "TODOS",
  );
  const [openForm, setOpenForm] = useState(false);
  // Conectar carga plata en la cuenta del vecino: mismo permiso que el traspaso.
  const puedeConectar = hasPermission(getAuth(), "ajustes.gestionar");
  // `?conexion=nueva&socio=<id>`: lo manda Socios tras dar de alta a alguien.
  const socioUrl = Number(params.get("socio"));
  const [openConexion, setOpenConexion] = useState(
    puedeConectar && params.get("conexion") === "nueva",
  );
  const [socioConexion] = useState<number | null>(
    params.get("conexion") === "nueva" && Number.isInteger(socioUrl) && socioUrl > 0
      ? socioUrl
      : null,
  );

  function cerrarConexion(): void {
    setOpenConexion(false);
    // Sin limpiar, recargar la página volvería a abrir el modal.
    if (params.has("conexion") || params.has("socio")) {
      const next = new URLSearchParams(params);
      next.delete("conexion");
      next.delete("socio");
      setParams(next, { replace: true });
    }
  }
  const [expanded, setExpanded] = useState<number | null>(null);

  const contratosQ = useQuery({
    queryKey: ["contratos", "todos"],
    queryFn: () => fetchContratos(),
    // El endpoint base sólo lista vigentes — para ver Finalizados hay que
    // llamar al GET con solo_vigentes=false. fetchContratos hardcodea solo
    // vigentes; por eso para padrón completo usamos directamente esta vista
    // operativa (con sus estados Activo/Cortado).
    // Finalizados se ven desde el detalle del socio.
  });

  const filtrados = useMemo(() => {
    if (!contratosQ.data) return [];
    const q = busqueda.trim().toLowerCase();
    return contratosQ.data.filter((c) => {
      const matchTexto =
        !q ||
        c.socio_nombre.toLowerCase().includes(q) ||
        c.socio_rut.toLowerCase().includes(q) ||
        (c.arranque_codigo?.toLowerCase().includes(q) ?? false) ||
        c.arranque_direccion.toLowerCase().includes(q);
      const matchEstado =
        filtroEstado === "TODOS" || c.estado === filtroEstado;
      return matchTexto && matchEstado;
    });
  }, [contratosQ.data, busqueda, filtroEstado]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Contratos"
        icon={FileText}
        subtitle={
          <>
            Vínculo Socio + Arranque + Tarifa. Un arranque sólo puede tener un
            contrato vigente. Cortar/reponer va por el módulo Cortes; finalizar
            libera el arranque.
            {contratosQ.data && (
              <span className="ml-1 tabular-nums">
                ({filtrados.length} de {contratosQ.data.length})
              </span>
            )}
          </>
        }
        actions={
        <>
          {/* Lo habitual es conectar a un vecino nuevo; "Nuevo contrato"
              sólo sirve sobre un arranque que ya existe. */}
          <Button
            variant={puedeConectar ? "secondary" : "primary"}
            icon={Plus}
            onClick={() => setOpenForm((v) => !v)}
            title="Contrato sobre un arranque que ya existe y está libre"
          >
            Nuevo contrato
          </Button>
          {puedeConectar && (
            <Button icon={Plug} onClick={() => setOpenConexion(true)}>
              Conexión nueva
            </Button>
          )}
        </>
        }
      />
      {openConexion && (
        <ConexionModal onClose={cerrarConexion} socioInicialId={socioConexion} />
      )}

      {openForm && <CrearContratoForm onClose={() => setOpenForm(false)} />}

      <Card padding="sm" className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <input
            type="search"
            placeholder="Buscar por socio, RUT, código o dirección…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-neutral-450 text-sm shadow-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-neutral-500" strokeWidth={1.5} />
          <select
            value={filtroEstado}
            onChange={(e) =>
              setFiltroEstado(e.target.value as "TODOS" | EstadoContrato)
            }
            className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
          >
            {ESTADOS_FILTRO.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
          <span className="text-xs text-neutral-600">
            (Finalizados sólo se ven desde la cuenta corriente del socio)
          </span>
        </div>
      </Card>

      {contratosQ.isPending && <Cargando />}
      {contratosQ.isError && (
        <ErrorCarga
          error={contratosQ.error}
          mensaje="Error al cargar contratos."
          onReintentar={() => void contratosQ.refetch()}
        />
      )}
      {contratosQ.data && filtrados.length === 0 && (
        contratosQ.data.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Aún no hay contratos"
            description="Un contrato vincula un socio + un arranque + una tarifa. Necesitas al menos uno antes de empezar a tomar lecturas."
          >
            <Button icon={Plus} onClick={() => setOpenForm(true)}>
              Crear primer contrato
            </Button>
          </EmptyState>
        ) : (
          <EmptyState
            icon={Search}
            tone="slate"
            title="Sin resultados"
            description="Prueba ajustando el filtro de estado o la búsqueda."
          />
        )
      )}

      {filtrados.length > 0 && (
        <TableWrap label="Contratos">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">{""}</th>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Arranque</th>
                <th scope="col" className="px-3 py-2 text-left">Sector</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtrados.map((c) => (
                <ContratoRow
                  key={c.id}
                  contrato={c}
                  expanded={expanded === c.id}
                  onToggle={() =>
                    setExpanded((p) => (p === c.id ? null : c.id))
                  }
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Fila + panel expandido
// ────────────────────────────────────────────────────────────────────────

function ContratoRow({
  contrato,
  expanded,
  onToggle,
}: {
  contrato: ContratoOperativo;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <>
      <tr className="hover:bg-neutral-50">
        <td className="px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={expanded ? ChevronDown : ChevronRight}
            onClick={onToggle}
            aria-label={expanded ? "Cerrar detalle" : "Abrir detalle"}
          />
        </td>
        <td className="px-3 py-2">
          <div className="font-medium text-neutral-900">{contrato.socio_nombre}</div>
          <div className="text-xs text-neutral-500">{contrato.socio_rut}</div>
        </td>
        <td className="px-3 py-2">
          <div className="font-mono text-xs">
            {contrato.arranque_codigo ?? "—"}
          </div>
          <div className="text-xs text-neutral-500">
            {contrato.arranque_direccion}
          </div>
        </td>
        <td className="px-3 py-2 text-neutral-600">
          {contrato.sector_nombre ?? "—"}
        </td>
        <td className="px-3 py-2">
          <EstadoBadge estado={contrato.estado as EstadoContrato} />
        </td>
        <td className="px-3 py-2 text-right">
          <Link
            to={`/socios/${contrato.socio_id}/cuenta-corriente?contrato_id=${contrato.id}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800"
          >
            Cuenta corriente
            <ArrowRight className="h-3 w-3" strokeWidth={1.5} />
          </Link>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-neutral-50">
          <td colSpan={6} className="px-3 py-3">
            <ContratoDetallePanel
              contratoId={contrato.id}
              direccion={contrato.arranque_direccion}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Panel de detalle (carga datos completos del contrato)
// ────────────────────────────────────────────────────────────────────────

function ContratoDetallePanel({
  contratoId,
  direccion,
}: {
  contratoId: number;
  direccion: string;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmFinalizar, setConfirmFinalizar] = useState(false);
  const [traspasando, setTraspasando] = useState(false);
  // Traspasar puede condonar deuda o cargarsela al titular nuevo, asi que el
  // backend exige `ajustes.gestionar` ademas de `contratos.write`. Ocultarlo
  // sin el permiso evita ofrecer un boton que va a devolver 403.
  const puedeTraspasar = hasPermission(getAuth(), "ajustes.gestionar");

  const detQ = useQuery({
    queryKey: ["contrato", contratoId],
    queryFn: () => obtenerContrato(contratoId),
  });
  const tarifasQ = useQuery({
    queryKey: ["tarifas"],
    queryFn: listarTarifas,
  });

  const finalizar = useMutation({
    mutationFn: (fecha: string | null) => finalizarContrato(contratoId, fecha),
    onSuccess: () => {
      toast.success("Contrato finalizado · arranque liberado");
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      void qc.invalidateQueries({ queryKey: ["contrato", contratoId] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo finalizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const reactivar = useMutation({
    mutationFn: () => reactivarContrato(contratoId),
    onSuccess: () => {
      toast.success("Contrato reactivado");
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      void qc.invalidateQueries({ queryKey: ["contrato", contratoId] });
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo reactivar";
      setError(msg);
      toast.error(msg);
    },
  });

  if (detQ.isPending) {
    return <Cargando inline texto="Cargando detalle…" />;
  }
  if (detQ.isError || !detQ.data) {
    return (
      <ErrorCarga
        error={detQ.error}
        mensaje="Error al cargar detalle."
        onReintentar={() => void detQ.refetch()}
      />
    );
  }
  const c = detQ.data;
  const tarifaActual = tarifasQ.data?.find((t) => t.id === c.tarifa_id);
  const finalizado = c.fecha_termino !== null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
        <Stat label="Inicio" value={c.fecha_inicio ? formatDate(c.fecha_inicio) : "—"} />
        <Stat
          label="Término"
          value={c.fecha_termino ? formatDate(c.fecha_termino) : "vigente"}
          tone={c.fecha_termino ? "muted" : "ok"}
        />
        <Stat
          label="Tarifa"
          value={tarifaActual?.nombre ?? `#${c.tarifa_id}`}
        />
        <Stat
          label="Arrendatario"
          value={c.es_arrendatario ? c.nombre_usuario ?? "Sí" : "No (socio dueño)"}
        />
      </div>

      {!editing ? (
        <div className="flex flex-wrap gap-2">
          {!finalizado && (
            <>
              {/* Pills de acción de estado: colores semánticos sin variante
                  equivalente → nativos */}
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 rounded-md bg-neutral-100 px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-200"
              >
                <Pencil className="h-3 w-3" strokeWidth={1.5} />
                Editar
              </button>
              {puedeTraspasar && (
                <Button
                  variant="secondary"
                  size="sm"
                  icon={ArrowLeftRight}
                  onClick={() => setTraspasando(true)}
                >
                  Traspasar derecho
                </Button>
              )}
              {traspasando && (
                <TraspasoModal
                  contratoId={contratoId}
                  socioActualId={c.socio_id}
                  direccion={direccion}
                  onClose={() => setTraspasando(false)}
                />
              )}
              <button
                type="button"
                onClick={() => setConfirmFinalizar(true)}
                disabled={finalizar.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-100 disabled:opacity-50"
              >
                <StopCircle className="h-3 w-3" strokeWidth={1.5} />
                Finalizar
              </button>
              <ConfirmDialog
                open={confirmFinalizar}
                tone="danger"
                title="Finalizar contrato"
                description="Finalizar el contrato. Esto libera el arranque para nuevo contrato. ¿Confirmas?"
                confirmLabel="Finalizar"
                loading={finalizar.isPending}
                onConfirm={() => {
                  setConfirmFinalizar(false);
                  finalizar.mutate(null);
                }}
                onCancel={() => setConfirmFinalizar(false)}
              />
            </>
          )}
          {finalizado && (
            <button
              type="button"
              onClick={() => reactivar.mutate()}
              disabled={reactivar.isPending}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-3 py-1.5 text-xs text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
            >
              <Undo className="h-3 w-3" strokeWidth={1.5} />
              Reactivar (si arranque libre)
            </button>
          )}
        </div>
      ) : (
        <EditarContratoForm
          contrato={c}
          tarifas={tarifasQ.data ?? []}
          onClose={() => setEditing(false)}
        />
      )}

      {error && <p className="text-sm text-rose-700">{error}</p>}
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "muted",
}: {
  label: string;
  value: string;
  tone?: "muted" | "ok";
}): JSX.Element {
  const cls =
    tone === "ok"
      ? "border-emerald-100 bg-emerald-50 text-emerald-900"
      : "border-neutral-100 bg-neutral-50 text-neutral-900";
  return (
    <div className={`rounded-md border p-2 ${cls}`}>
      <div className="text-xs uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta
// ────────────────────────────────────────────────────────────────────────

function CrearContratoForm({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const sociosQ = useQuery({
    queryKey: ["socios", { only_active: true, only_socios: false }],
    queryFn: () => listarSocios({ only_active: true, only_socios: false }),
  });
  const arranquesQ = useQuery({
    queryKey: ["arranques", "todos"],
    queryFn: () => listarArranques(),
  });
  const tarifasQ = useQuery({
    queryKey: ["tarifas"],
    queryFn: listarTarifas,
  });
  const contratosVigentesQ = useQuery({
    queryKey: ["contratos", "todos"],
    queryFn: () => fetchContratos(),
  });

  // Arranques disponibles = los que no tienen contrato vigente
  const arranquesDisponibles = useMemo(() => {
    const ocupados = new Set(
      (contratosVigentesQ.data ?? []).map((c) => c.arranque_id),
    );
    return (arranquesQ.data ?? []).filter((a) => !ocupados.has(a.id));
  }, [arranquesQ.data, contratosVigentesQ.data]);

  const [socioId, setSocioId] = useState("");
  const [arranqueId, setArranqueId] = useState("");
  const [tarifaId, setTarifaId] = useState("");
  const [fechaInicio, setFechaInicio] = useState(hoyLocalISO());
  const [esArrendatario, setEsArrendatario] = useState(false);
  const [nombreUsuario, setNombreUsuario] = useState("");
  const [rutUsuario, setRutUsuario] = useState("");
  const [telUsuario, setTelUsuario] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (p: ContratoCreatePayload) => crearContrato(p),
    onSuccess: (c) => {
      toast.success(`Contrato #${c.id} creado`);
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    socioId, arranqueId, tarifaId, fechaInicio, esArrendatario, nombreUsuario,
    rutUsuario, telUsuario,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nuevo contrato"
      icon={FileText}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (!socioId) return setError("Elige un socio");
              if (!arranqueId) return setError("Elige un arranque libre");
              if (!tarifaId) return setError("Elige una tarifa");
              mut.mutate({
                socio_id: Number(socioId),
                arranque_id: Number(arranqueId),
                tarifa_id: Number(tarifaId),
                fecha_inicio: fechaInicio || null,
                es_arrendatario: esArrendatario,
                nombre_usuario: esArrendatario
                  ? nombreUsuario.trim() || null
                  : null,
                rut_usuario: esArrendatario ? rutUsuario.trim() || null : null,
                telefono_usuario: esArrendatario
                  ? telUsuario.trim() || null
                  : null,
              });
            }}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Creando…" : "Crear contrato"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Select
          label="Socio"
          required
          value={socioId}
          onChange={(e) => setSocioId(e.target.value)}
          disabled={mut.isPending}
        >
          <option value="">— elegir —</option>
          {(sociosQ.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.nombre} · {s.rut}
            </option>
          ))}
        </Select>
        <Select
          label={`Arranque (${arranquesDisponibles.length} libre${arranquesDisponibles.length === 1 ? "" : "s"})`}
          value={arranqueId}
          onChange={(e) => setArranqueId(e.target.value)}
          disabled={mut.isPending}
        >
          <option value="">— elegir —</option>
          {arranquesDisponibles.map((a) => (
            <option key={a.id} value={a.id}>
              {a.codigo_arranque ?? `#${a.id}`} · {a.direccion}
            </option>
          ))}
        </Select>
        <Select
          label="Tarifa"
          required
          value={tarifaId}
          onChange={(e) => setTarifaId(e.target.value)}
          disabled={mut.isPending}
        >
          <option value="">— elegir —</option>
          {(tarifasQ.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </Select>

        <Input
          label="Fecha inicio"
          type="date"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          disabled={mut.isPending}
        />
      </div>

      <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
        <input
          type="checkbox"
          checked={esArrendatario}
          onChange={(e) => setEsArrendatario(e.target.checked)}
          disabled={mut.isPending}
        />
        El ocupante es arrendatario (distinto al socio dueño)
      </label>

      {esArrendatario && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            label="Nombre arrendatario"
            type="text"
            maxLength={200}
            value={nombreUsuario}
            onChange={(e) => setNombreUsuario(e.target.value)}
            disabled={mut.isPending}
          />
          <Input
            label="RUT arrendatario"
            type="text"
            maxLength={12}
            value={rutUsuario}
            onChange={(e) => setRutUsuario(e.target.value)}
            disabled={mut.isPending}
          />
          <Input
            label="Teléfono arrendatario"
            type="tel"
            maxLength={20}
            value={telUsuario}
            onChange={(e) => setTelUsuario(e.target.value)}
            disabled={mut.isPending}
          />
        </div>
      )}

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form edición (tarifa + arrendatario)
// ────────────────────────────────────────────────────────────────────────

function EditarContratoForm({
  contrato,
  tarifas,
  onClose,
}: {
  contrato: ContratoRead;
  tarifas: TarifaRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [tarifaId, setTarifaId] = useState(String(contrato.tarifa_id));
  const [esArr, setEsArr] = useState(contrato.es_arrendatario);
  const [nombre, setNombre] = useState(contrato.nombre_usuario ?? "");
  const [rut, setRut] = useState(contrato.rut_usuario ?? "");
  const [tel, setTel] = useState(contrato.telefono_usuario ?? "");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (p: ContratoUpdatePayload) =>
      actualizarContrato(contrato.id, p),
    onSuccess: () => {
      toast.success("Contrato actualizado");
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      void qc.invalidateQueries({ queryKey: ["contrato", contrato.id] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([tarifaId, esArr, nombre, rut, tel]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Editar contrato"
      icon={FileText}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() =>
              mut.mutate({
                tarifa_id: Number(tarifaId),
                es_arrendatario: esArr,
                nombre_usuario: esArr ? nombre.trim() || null : null,
                rut_usuario: esArr ? rut.trim() || null : null,
                telefono_usuario: esArr ? tel.trim() || null : null,
              })
            }
            disabled={mut.isPending}
          >
            {mut.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          label="Tarifa"
          value={tarifaId}
          onChange={(e) => setTarifaId(e.target.value)}
          disabled={mut.isPending}
        >
          {tarifas.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </Select>
        {tarifaId !== String(contrato.tarifa_id) && (
          <p className="-mt-1 text-xs text-amber-700">
            La tarifa nueva se aplica también a las lecturas de este contrato
            que todavía no se facturaron. El cambio queda en la bitácora.
          </p>
        )}

        <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={esArr}
            onChange={(e) => setEsArr(e.target.checked)}
            disabled={mut.isPending}
          />
          Arrendatario distinto al socio
        </label>

        {esArr && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              label="Nombre"
              type="text"
              maxLength={200}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={mut.isPending}
            />
            <Input
              label="RUT"
              type="text"
              maxLength={12}
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              disabled={mut.isPending}
            />
            <Input
              label="Teléfono"
              type="tel"
              maxLength={20}
              value={tel}
              onChange={(e) => setTel(e.target.value)}
              disabled={mut.isPending}
            />
          </div>
        )}

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

function EstadoBadge({ estado }: { estado: EstadoContrato }): JSX.Element {
  const map: Record<EstadoContrato, BadgeTone> = {
    Activo: "emerald",
    Cortado: "rose",
    Finalizado: "slate",
  };
  return <Badge tone={map[estado]}>{estado}</Badge>;
}

